import { lazy, Suspense } from 'react';
import { ThemeProvider } from '@/components/theme-provider';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { AuthProvider, useAuth } from '@/lib/auth';
import { AdminAuthProvider, useAdminAuth } from '@/lib/admin-auth';
import { Toaster } from 'sonner';

// Configure router with future flags
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
const router = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

// Preload key routes
const PortfolioPage = lazy(() => {
  const component = import('@/components/dashboard/Dashboard');
  component.catch(() => {});
  return component;
});

const UebersichtPage = lazy(() => {
  const component = import('@/components/pages/UebersichtPage');
  component.catch(() => {});
  return component;
});

const EntwicklungPage = lazy(() => {
  const component = import('@/components/pages/EntwicklungPage');
  component.catch(() => {});
  return component;
});

const TodosPage = lazy(() => {
  const component = import('@/components/pages/TodosPage');
  component.catch(() => {});
  return component;
});

const AcademyPage = lazy(() => {
  const component = import('@/components/academy/AcademyPage');
  component.catch(() => {});
  return component;
});

const CoursePage = lazy(() => {
  const component = import('@/components/academy/CoursePage');
  component.catch(() => {});
  return component;
});

const ProfilePage = lazy(() => {
  const component = import('@/components/profile/ProfilePage');
  component.catch(() => {});
  return component;
});

const CommunityPage = lazy(() => {
  const component = import('@/components/community/CommunityPage');
  component.catch(() => {});
  return component;
});

const RegisterPage = lazy(() => {
  const component = import('@/components/auth/RegisterPage');
  component.catch(() => {});
  return component;
});

const LoginPage = lazy(() => {
  const component = import('@/components/auth/LoginPage');
  component.catch(() => {});
  return component;
});

const LoadingTransition = lazy(() => {
  const component = import('@/components/LoadingTransition');
  component.catch(() => {});
  return component;
});

const TaxSimulationPage = lazy(() => {
  const component = import('@/components/pages/TaxSimulationPage');
  component.catch(() => {});
  return component;
});

const ProductsPage = lazy(() => {
  const component = import('@/components/pages/ProductsPage');
  component.catch(() => {});
  return component;
});

const SuccessPage = lazy(() => {
  const component = import('@/components/pages/SuccessPage');
  component.catch(() => {});
  return component;
});

const CancelPage = lazy(() => {
  const component = import('@/components/pages/CancelPage');
  component.catch(() => {});
  return component;
});

const ForgotPasswordPage = lazy(() => {
  const component = import('@/components/auth/ForgotPasswordPage');
  component.catch(() => {});
  return component;
});

const UpdatePasswordPage = lazy(() => {
  const component = import('@/components/auth/UpdatePasswordPage');
  component.catch(() => {});
  return component;
});

// Admin Pages
const AdminLoginPage = lazy(() => {
  const component = import('@/components/auth/AdminLoginPage');
  component.catch(() => {});
  return component;
});

const AdminLayout = lazy(() => {
  const component = import('@/components/admin/AdminLayout');
  component.catch(() => {});
  return component;
});

const AdminDashboard = lazy(() => {
  const component = import('@/components/admin/AdminDashboard');
  component.catch(() => {});
  return component;
});

const AdminPage = lazy(() => {
  const component = import('@/components/admin/AdminPage');
  component.catch(() => {});
  return component;
});

const AdminDebugPage = lazy(() => {
  const component = import('@/components/admin/AdminDebugPage');
  component.catch(() => {});
  return component;
});

const BetaLandingPage = lazy(() => {
  const component = import('@/components/auth/BetaLandingPage');
  component.catch(() => {});
  return component;
});

const BetaUploadPage = lazy(() => {
  const component = import('@/components/beta/BetaUploadPage');
  component.catch(() => {});
  return component;
});

const BetaThankYouPage = lazy(() => {
  const component = import('@/components/beta/BetaThankYouPage');
  component.catch(() => {});
  return component;
});

const BetaCustomersTab = lazy(() => {
  const component = import('@/components/admin/BetaCustomersTab');
  component.catch(() => {});
  return component;
});

// Loading component with skeleton UI
function LoadingFallback() {
  return (
    <div className="min-h-screen bg-white/80 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-7xl mx-auto p-8">
        <div className="h-8 w-64 bg-neutral-200 rounded-lg animate-pulse mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-neutral-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading, customerData } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  // Redirect beta users to upload page if they haven't uploaded yet
  if (customerData?.is_beta_user && customerData?.beta_status === 'pending_upload') {
    if (location.pathname !== '/beta/upload') {
      return <Navigate to="/beta/upload" replace />;
    }
  }

  // Redirect beta users who have uploaded to thank you page
  if (customerData?.is_beta_user && customerData?.beta_status === 'uploaded') {
    if (location.pathname !== '/beta/thank-you') {
      return <Navigate to="/beta/thank-you" replace />;
    }
  }

  return children;
}

function RequireAdminAuth({ children }: { children: JSX.Element }) {
  const { admin, loading } = useAdminAuth();
  const location = useLocation();

  console.log('RequireAdminAuth - admin:', admin, 'loading:', loading);

  if (loading) {
    return <LoadingFallback />;
  }

  if (!admin) {
    console.log('No admin, redirecting to admin login');
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <Router future={router}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={
                <Suspense fallback={<LoadingFallback />}>
                  <BetaLandingPage />
                </Suspense>
              } />
              <Route path="/register" element={
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterPage />
                </Suspense>
              } />
              <Route path="/login" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LoginPage />
                </Suspense>
              } />
              
              <Route path="/passwort-vergessen" element={
                <Suspense fallback={<LoadingFallback />}>
                  <ForgotPasswordPage />
                </Suspense>
              } />
              
              <Route path="/update-password" element={
                <Suspense fallback={<LoadingFallback />}>
                  <UpdatePasswordPage />
                </Suspense>
              } />
              
              {/* Stripe success/cancel pages (public) */}
              <Route path="/success" element={
                <Suspense fallback={<LoadingFallback />}>
                  <SuccessPage />
                </Suspense>
              } />
              <Route path="/cancel" element={
                <Suspense fallback={<LoadingFallback />}>
                  <CancelPage />
                </Suspense>
              } />
              
              {/* Post-login transition route */}
              <Route path="/transition-to-dashboard" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LoadingTransition />
                </Suspense>
              } />
              
              {/* Beta routes */}
              <Route path="/beta/upload" element={
                <RequireAuth>
                  <Suspense fallback={<LoadingFallback />}>
                    <BetaUploadPage />
                  </Suspense>
                </RequireAuth>
              } />
              <Route path="/beta/thank-you" element={
                <RequireAuth>
                  <Suspense fallback={<LoadingFallback />}>
                    <BetaThankYouPage />
                  </Suspense>
                </RequireAuth>
              } />

              {/* Admin login route (public) */}
              <Route path="/admin/login" element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminLoginPage />
                </Suspense>
              } />
              
              {/* Protected admin routes */}
              <Route path="/admin" element={
                <RequireAdminAuth>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminLayout />
                  </Suspense>
                </RequireAdminAuth>
              }>
                <Route index element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="beta-customers" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <BetaCustomersTab />
                  </Suspense>
                } />
                <Route path="customers" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminPage />
                  </Suspense>
                } />
                <Route path="properties" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminPage />
                  </Suspense>
                } />
                <Route path="analytics" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="debug" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDebugPage />
                  </Suspense>
                } />
                <Route path="settings" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                } />
              </Route>
              
              {/* Protected customer routes */}
              <Route path="/dashboard" element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }>
                <Route index element={<Navigate to="/optimierung" replace />} />
                
                {/* Customer routes */}
                <Route path="portfoliopage" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PortfolioPage />
                  </Suspense>
                } />
                <Route path="optimierung" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <UebersichtPage />
                  </Suspense>
                } />
                <Route path="performance" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <EntwicklungPage />
                  </Suspense>
                } />
                <Route path="wertentwicklung" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <TodosPage />
                  </Suspense>
                } />
                <Route path="steuersimulation" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <TaxSimulationPage />
                  </Suspense>
                } />
                <Route path="products" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ProductsPage />
                  </Suspense>
                } />
                <Route path="akademie" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <AcademyPage />
                  </Suspense>
                } />
                <Route path="akademie/:courseId" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <CoursePage />
                  </Suspense>
                } />
                <Route path="profil" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ProfilePage />
                  </Suspense>
                } />
                <Route path="community" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <CommunityPage />
                  </Suspense>
                } />
              </Route>
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </Router>
        </AdminAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;